import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Archive,
  Download,
  Eye,
  File,
  FileText,
  FolderOpen,
  LayoutGrid,
  Image,
  List,
  Music,
  Plus,
  Search,
  Trash2,
  Video,
} from 'lucide-react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import FileUploadModal from '../components/Files/FileUploadModal';
import FileViewerModal from '../components/Licenses/FileViewerModal';
import { getAppHomePath } from '../constants/navigation';
import { fileService } from '../services/api';
import { userFacingError } from '../i18n/userFacingError';

const SOURCE_LABEL = {
  upload: 'Upload',
  condicionante: 'Condicionante',
  checklist: 'Checklist',
  licenca_pdf: 'Licença',
};

const CATEGORIES = [
  { value: 'all', label: 'Todos', icon: File },
  { value: 'document', label: 'Documentos', icon: FileText },
  { value: 'upload', label: 'Upload', icon: FolderOpen },
  { value: 'condicionante', label: 'Condicionante', icon: FileText },
  { value: 'checklist', label: 'Checklist', icon: FileText },
  { value: 'licenca_pdf', label: 'Licença', icon: FileText },
  { value: 'image', label: 'Imagens', icon: Image },
  { value: 'video', label: 'Vídeos', icon: Video },
  { value: 'audio', label: 'Áudios', icon: Music },
  { value: 'archive', label: 'Compactados', icon: Archive },
];

const SOURCE_FILTERS = new Set(['upload', 'condicionante', 'checklist', 'licenca_pdf']);

function inferFileType(file) {
  const mime = String(file.mime_type || '').toLowerCase();
  const name = String(file.filename || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
  if (mime.startsWith('video/') || /\.(mp4|avi|mov|wmv|webm|mkv|flv)$/.test(name)) return 'video';
  if (mime.startsWith('audio/') || /\.(mp3|wav|flac|aac|ogg)$/.test(name)) return 'audio';
  if (mime.includes('zip') || mime.includes('rar') || /\.(zip|rar|7z|tar|gz)$/.test(name)) return 'archive';
  return 'document';
}

function rowKey(file) {
  return `${file.source}-${file.id}`;
}

function getFileIcon(type) {
  switch (type) {
    case 'image':
      return Image;
    case 'video':
      return Video;
    case 'audio':
      return Music;
    case 'archive':
      return Archive;
    default:
      return FileText;
  }
}

function getFileColor(type) {
  switch (type) {
    case 'image':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'video':
      return 'text-purple-700 bg-purple-50 border-purple-200';
    case 'audio':
      return 'text-orange-700 bg-orange-50 border-orange-200';
    case 'document':
      return 'text-blue-700 bg-blue-50 border-blue-200';
    case 'archive':
      return 'text-gray-700 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-700 bg-gray-50 border-gray-200';
  }
}

function getTypeLabel(type) {
  switch (type) {
    case 'image':
      return 'Imagem';
    case 'video':
      return 'Vídeo';
    case 'audio':
      return 'Áudio';
    case 'document':
      return 'Documento';
    case 'archive':
      return 'Compactado';
    default:
      return type || 'Arquivo';
  }
}

function formatFileSize(bytes) {
  if (bytes == null || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

async function downloadBlob(file) {
  const r = await fileService.getBlob(file.source, file.id);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename || 'arquivo';
  a.click();
  URL.revokeObjectURL(url);
}

function FileActions({ file, compact, downloadingId, deletingId, onView, onDownload, onDelete }) {
  const key = rowKey(file);
  return (
    <div className={`flex ${compact ? 'flex-wrap' : 'items-center'} gap-2`}>
      <button
        type="button"
        onClick={() => onView(file)}
        className={`btn btn-outline btn-sm inline-flex items-center gap-1.5 ${compact ? 'min-w-0 flex-1' : ''}`}
      >
        <Eye className="h-4 w-4 shrink-0" />
        Ver
      </button>
      <button
        type="button"
        onClick={() => onDownload(file)}
        disabled={downloadingId === key}
        className={`btn btn-outline btn-sm inline-flex items-center gap-1.5 ${compact ? 'min-w-0 flex-1' : ''}`}
      >
        <Download className="h-4 w-4 shrink-0" />
        {downloadingId === key ? '...' : 'Baixar'}
      </button>
      <button
        type="button"
        onClick={() => onDelete(file)}
        disabled={deletingId === key}
        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
        title="Excluir"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Files() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [layout, setLayout] = useState('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery(['arquivos'], async () => {
    const r = await fileService.list();
    return Array.isArray(r.data) ? r.data : [];
  });

  const files = useMemo(
    () =>
      (data || []).map((row) => ({
        ...row,
        file_type: inferFileType(row),
      })),
    [data]
  );

  const filteredFiles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return files.filter((file) => {
      const matchesSearch =
        !q ||
        String(file.filename || '')
          .toLowerCase()
          .includes(q) ||
        String(file.description || '')
          .toLowerCase()
          .includes(q) ||
        String(file.tags || '')
          .toLowerCase()
          .includes(q) ||
        String(SOURCE_LABEL[file.source] || file.source)
          .toLowerCase()
          .includes(q);
      if (!matchesSearch) return false;
      if (filterCategory === 'all') return true;
      if (SOURCE_FILTERS.has(filterCategory)) return file.source === filterCategory;
      return file.file_type === filterCategory;
    });
  }, [files, searchTerm, filterCategory]);

  const handleDownload = async (file) => {
    const key = rowKey(file);
    setDownloadingId(key);
    try {
      await downloadBlob(file);
    } catch (err) {
      toast.error(userFacingError(err, 'downloadError'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (file) => {
    const label = file.filename || 'este arquivo';
    const extra =
      file.source === 'upload'
        ? ''
        : ' O anexo também será removido da licença, condicionante ou checklist de origem.';
    if (!window.confirm(`Excluir "${label}"?${extra}`)) return;
    const key = rowKey(file);
    setDeletingId(key);
    try {
      await fileService.deleteBySource(file.source, file.id);
      toast.success('Arquivo excluído');
      queryClient.invalidateQueries(['arquivos']);
      if (viewer && rowKey(viewer) === key) setViewer(null);
    } catch (err) {
      toast.error(userFacingError(err, 'requestError'));
    } finally {
      setDeletingId(null);
    }
  };

  const actionProps = {
    downloadingId,
    deletingId,
    onView: setViewer,
    onDownload: handleDownload,
    onDelete: handleDelete,
  };

  if (isLoading) {
    return (
      <FormPageShell maxWidth="max-w-7xl">
        <div className="flex min-h-[320px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        </div>
      </FormPageShell>
    );
  }

  if (isError) {
    return (
      <FormPageShell maxWidth="max-w-7xl">
        <PageHeader
          title="Arquivos"
          description="Central de anexos do licenciamento."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Arquivos' }]}
        />
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>Não foi possível carregar os arquivos.</span>
          </div>
          <button type="button" onClick={() => refetch()} className="btn btn-primary mt-4">
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell maxWidth="max-w-7xl">
      <PageHeader
        title="Arquivos"
        description="Todos os anexos do ambiental — uploads, condicionantes, checklists e PDFs de licença."
        breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Arquivos' }]}
      >
        <button
          type="button"
          onClick={() => setShowUploadModal(true)}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Upload de arquivo
        </button>
      </PageHeader>

      <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, origem ou tag"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-full pl-10"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5 rounded-lg bg-gray-100 p-1">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setFilterCategory(cat.value)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      filterCategory === cat.value
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
            <div className="flex border-l border-gray-200 pl-2">
              <button
                type="button"
                onClick={() => setLayout('grid')}
                className={`rounded-lg p-2 ${layout === 'grid' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:bg-gray-100'}`}
                title="Grade"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setLayout('list')}
                className={`rounded-lg p-2 ${layout === 'list' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:bg-gray-100'}`}
                title="Lista"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-500">
        {filteredFiles.length} {filteredFiles.length === 1 ? 'arquivo' : 'arquivos'}
      </p>

      {layout === 'grid' ? (
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredFiles.map((file) => {
            const FileIcon = getFileIcon(file.file_type);
            const colorClass = getFileColor(file.file_type);
            return (
              <div
                key={rowKey(file)}
                className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${colorClass}`}>
                    <FileIcon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-gray-900" title={file.filename}>
                      {file.filename}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${colorClass}`}>
                        {getTypeLabel(file.file_type)}
                      </span>
                      <span className="inline-block rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {SOURCE_LABEL[file.source] || file.source}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      {formatFileSize(file.file_size)} · {formatDate(file.created_at)}
                    </div>
                    {file.description && <p className="mt-1 line-clamp-2 text-sm text-gray-600">{file.description}</p>}
                    {file.tags && <p className="mt-1 truncate text-xs text-gray-400">{file.tags}</p>}
                  </div>
                </div>
                <div className="mt-4">
                  <FileActions file={file} compact {...actionProps} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <ul className="divide-y divide-gray-200">
            {filteredFiles.map((file) => {
              const FileIcon = getFileIcon(file.file_type);
              const colorClass = getFileColor(file.file_type);
              return (
                <li key={rowKey(file)} className="flex items-center gap-4 p-4 transition-colors hover:bg-gray-50">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${colorClass}`}>
                    <FileIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-medium text-gray-900">{file.filename}</h3>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                      <span>{getTypeLabel(file.file_type)}</span>
                      <span>{SOURCE_LABEL[file.source] || file.source}</span>
                      <span>{formatFileSize(file.file_size)}</span>
                      <span>{formatDate(file.created_at)}</span>
                      {file.description && <span className="max-w-[200px] truncate">{file.description}</span>}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <FileActions file={file} {...actionProps} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {filteredFiles.length === 0 && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white py-16 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum arquivo encontrado</h3>
          <p className="mb-4 text-gray-500">Ajuste os filtros ou faça upload de novos arquivos.</p>
          <button type="button" onClick={() => setShowUploadModal(true)} className="btn btn-primary">
            Fazer upload
          </button>
        </div>
      )}

      {viewer && (
        <FileViewerModal
          anexo={{
            id: viewer.id,
            filename_original: viewer.filename,
            mime_type: viewer.mime_type,
          }}
          loadBlob={() => fileService.getBlob(viewer.source, viewer.id)}
          onClose={() => setViewer(null)}
        />
      )}

      <FileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => queryClient.invalidateQueries(['arquivos'])}
      />
    </FormPageShell>
  );
}
